import React from 'react'
import { Link } from 'react-router-dom'
import Logo from '../common/Logo'
import {
  Mail, 
  MapPin, 
  BookOpen, 
  GraduationCap, 
  FileText, 
  Users,
  Star,
  Award,
  ExternalLink
} from 'lucide-react'

/**
 * Rodapé público para páginas de SEO
 * Inclui links importantes, informações da empresa e recursos educacionais
 */
const PublicFooter: React.FC = () => {
  const currentYear = new Date().getFullYear()

  return (
    <footer className="bg-cream-100 dark:bg-ink-900 text-ink-900 dark:text-cream-50 pt-16 pb-8 border-t border-cream-400 dark:border-ink-500">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Coluna 1 - Sobre a Atividade Pronta */}
          <div>
            <div className="flex items-center mb-6">
              <Logo variant="lockup" size={40} />
            </div>
            <p className="text-ink-500 dark:text-ink-300 mb-6">
              Plataforma completa para professores criarem e gerenciarem avaliações educacionais com banco de questões completo e geração de PDFs otimizados.
            </p>
            <div className="flex space-x-4">
              <div className="flex items-center text-sm text-ink-500 dark:text-ink-300">
                <Users className="w-4 h-4 mr-2" />
                <span>Feito por professores, para professores</span>
              </div>
            </div>
          </div>
          
          {/* Coluna 2 - Recursos e Funcionalidades */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-ink-900 dark:text-cream-50">Recursos</h3>
            <ul className="space-y-4">
              <li>
                <Link
                  to="/avaliacoes"
                  className="flex items-center text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  <BookOpen className="w-4 h-4 mr-3" />
                  Banco de Atividades
                </Link>
              </li>
              <li>
                <a
                  href="/#features"
                  className="flex items-center text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  <FileText className="w-4 h-4 mr-3" />
                  Editor de Avaliações
                </a>
              </li>
              <li>
                <a
                  href="/#features"
                  className="flex items-center text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  <GraduationCap className="w-4 h-4 mr-3" />
                  Banco de Questões
                </a>
              </li>
              <li>
                <a
                  href="/#features"
                  className="flex items-center text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  <Award className="w-4 h-4 mr-3" />
                  Modelos Prontos
                </a>
              </li>
              <li>
                <a
                  href="/#features"
                  className="flex items-center text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  <Star className="w-4 h-4 mr-3" />
                  Modelos prontos
                </a>
              </li>
            </ul>
          </div>
          
          {/* Coluna 3 - Categorias Populares */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-ink-900 dark:text-cream-50">Categorias Populares</h3>
            <ul className="space-y-4">
              <li>
                <Link
                  to="/avaliacoes?disciplina=Matemática"
                  className="text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  Matemática
                </Link>
              </li>
              <li>
                <Link
                  to="/avaliacoes?disciplina=Língua Portuguesa"
                  className="text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  Língua Portuguesa
                </Link>
              </li>
              <li>
                <Link
                  to="/avaliacoes?disciplina=História"
                  className="text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  História
                </Link>
              </li>
              <li>
                <Link
                  to="/avaliacoes?disciplina=Geografia"
                  className="text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  Geografia
                </Link>
              </li>
              <li>
                <Link
                  to="/avaliacoes?disciplina=Ciências"
                  className="text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  Ciências
                </Link>
              </li>
              <li>
                <Link
                  to="/avaliacoes?serie=Ensino Fundamental I"
                  className="text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  Ensino Fundamental I
                </Link>
              </li>
              <li>
                <Link
                  to="/avaliacoes?serie=Ensino Fundamental II"
                  className="text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  Ensino Fundamental II
                </Link>
              </li>
              <li>
                <Link
                  to="/avaliacoes?serie=Ensino Médio"
                  className="text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  Ensino Médio
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Coluna 4 - Empresa e Contato */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-ink-900 dark:text-cream-50">Empresa</h3>
            <ul className="space-y-4">
              <li>
                <Link
                  to="/about"
                  className="text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  Sobre Nós
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  Contato
                </Link>
              </li>
              <li>
                <a
                  href="/#pricing"
                  className="text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  Planos e Preços
                </a>
              </li>
              <li>
                <Link
                  to="/planos/personalizado"
                  className="text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  Para redes e secretarias
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  Termos de Uso
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  Política de Privacidade
                </Link>
              </li>
              <li>
                <Link
                  to="/lgpd"
                  className="text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors"
                >
                  Política LGPD
                </Link>
              </li>
            </ul>

            <div className="mt-8">
              <h4 className="text-sm font-semibold mb-4 text-ink-900 dark:text-cream-50">Contato</h4>
              <div className="space-y-3">
                <div className="flex items-start">
                  <Mail className="w-4 h-4 text-ink-400 dark:text-ink-300 mr-3 mt-0.5" />
                  <a
                    href="mailto:contato@atividadepronta.com.br"
                    className="text-ink-500 dark:text-ink-300 hover:text-terra-500 dark:hover:text-terra-300 transition-colors text-sm"
                  >
                    contato@atividadepronta.com.br
                  </a>
                </div>
                <div className="flex items-start">
                  <MapPin className="w-4 h-4 text-ink-400 dark:text-ink-300 mr-3 mt-0.5" />
                  <span className="text-ink-500 dark:text-ink-300 text-sm">
                    Brasil - Atendimento online
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Seção inferior - Copyright e Links Legais */}
        <div className="border-t border-cream-400 dark:border-ink-500 pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-ink-500 dark:text-ink-400 text-sm mb-4 md:mb-0">
              &copy; {currentYear} Atividade Pronta. Todos os direitos reservados.
            </p>
            <div className="flex flex-wrap justify-center md:justify-end space-x-6">
              <Link
                to="/terms"
                className="text-ink-500 dark:text-ink-400 hover:text-terra-500 dark:hover:text-terra-300 text-sm transition-colors"
              >
                Termos
              </Link>
              <Link
                to="/privacy"
                className="text-ink-500 dark:text-ink-400 hover:text-terra-500 dark:hover:text-terra-300 text-sm transition-colors"
              >
                Privacidade
              </Link>
              <Link
                to="/cookies"
                className="text-ink-500 dark:text-ink-400 hover:text-terra-500 dark:hover:text-terra-300 text-sm transition-colors"
              >
                Cookies
              </Link>
              <Link
                to="/lgpd"
                className="text-ink-500 dark:text-ink-400 hover:text-terra-500 dark:hover:text-terra-300 text-sm transition-colors"
              >
                LGPD
              </Link>
              <a
                href="/sitemap.xml"
                className="text-ink-500 dark:text-ink-400 hover:text-terra-500 dark:hover:text-terra-300 text-sm transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                Sitemap
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default PublicFooter
